import { mapState } from 'pinia'
import { defineAsyncComponent } from 'vue'

import ColorInput from 'src/components/color_input/color_input.vue'
import FollowButton from 'src/components/follow_button/follow_button.vue'
import ProgressButton from 'src/components/progress_button/progress_button.vue'
import Select from 'src/components/select/select.vue'
import UserAvatar from 'src/components/user_avatar/user_avatar.vue'
import UserLink from 'src/components/user_link/user_link.vue'

import { useInstanceStore } from 'src/stores/instance.js'
import { useInterfaceStore } from 'src/stores/interface'
import { useMediaViewerStore } from 'src/stores/media_viewer'
import { useMergedConfigStore } from 'src/stores/merged_config.js'
import { usePostStatusStore } from 'src/stores/post_status'
import { useUserHighlightStore } from 'src/stores/user_highlight.js'
import { useUsersStore } from 'src/stores/users.js'

import { serverUrl } from 'src/api/api_base.js'
import { formatLongDate } from 'src/services/date_utils/date_utils.js'
import localeService from 'src/services/locale/locale.service.js'
import { profileLink } from 'src/services/user_display/user_display.js'

const KNOWN_TAGS = new Set([
  'mrf_tag:media-force-nsfw',
  'mrf_tag:media-strip',
  'mrf_tag:force-unlisted',
  'mrf_tag:sandbox',
  'mrf_tag:disable-remote-subscription',
  'mrf_tag:disable-any-subscription',
])

export default {
  props: {
    // Show this instead of the stored profile: the settings tab's live
    // preview of unsaved changes. All strings are HTML.
    // { name, bio, fields: [{ name, value }], avatar, banner }
    preview: {
      required: false,
      default: null,
      type: Object,
    },
    // ID of user to show data of
    userId: {
      required: true,
      type: String,
    },
    // Use a compact layout that hides bio, stats etc.
    hideBio: {
      required: false,
      default: false,
      type: Boolean,
    },
    // Hide action buttons
    hideButtons: {
      required: false,
      default: false,
      type: Boolean,
    },
    // default - open profile, 'zoom' - zoom, function - call function
    avatarAction: {
      required: false,
      type: String,
      default: 'default',
    },
    // Show note editor if supported
    hasNoteEditor: {
      required: false,
      type: Boolean,
      default: false,
    },
    // Show close icon (for popovers)
    showClose: {
      required: false,
      type: Boolean,
      default: false,
    },
    // Show close icon (for popovers)
    showExpand: {
      required: false,
      type: Boolean,
      default: false,
    },
    // Disable forced 3:1 aspect ratio
    compact: {
      required: false,
      type: Boolean,
      default: false,
    },
  },
  components: {
    UserAvatar,
    RemoteFollow: defineAsyncComponent(
      () => import('src/components/remote_follow/remote_follow.vue'),
    ),
    ModerationTools: defineAsyncComponent(
      () => import('src/components/moderation_tools/moderation_tools.vue'),
    ),
    AccountActions: defineAsyncComponent(
      () => import('src/components/account_actions/account_actions.vue'),
    ),
    ProgressButton,
    FollowButton,
    Select,
    UserLink,
    UserNote: defineAsyncComponent(
      () => import('src/components/user_note/user_note.vue'),
    ),
    UserTimedFilterModal: defineAsyncComponent(
      () =>
        import(
          'src/components/user_timed_filter_modal/user_timed_filter_modal.vue'
        ),
    ),
    ColorInput,
  },
  created() {
    useUsersStore().fetchUserRelationship(this.user.id)
  },
  watch: {
    // a profile reuses its card when going from one user to the next
    userId(id) {
      useUsersStore().fetchUserRelationship(id)
    },
  },
  computed: {
    user() {
      return useUsersStore().findUser(this.userId)
    },
    displayName() {
      return this.preview?.name ?? this.user.name_unescaped
    },
    bioHtml() {
      return this.preview?.bio ?? this.user.description_html
    },
    profileFields() {
      return this.preview?.fields ?? this.user.fields_html
    },
    relationship() {
      return useUsersStore().relationship(this.userId)
    },
    isOtherUser() {
      return this.user.id !== useUsersStore().currentUser?.id
    },
    loggedIn() {
      return useUsersStore().currentUser
    },
    dailyAvg() {
      const days = Math.ceil(
        (new Date() - new Date(this.user.created_at)) / (60 * 60 * 24 * 1000),
      )
      return Math.round(this.user.statuses_count / days)
    },
    userHighlightType: {
      get() {
        return useUserHighlightStore().get(this.user.screen_name).type
      },
      set(type) {
        if (type !== 'disabled') {
          useUserHighlightStore().setAndSave({
            user: this.user.screen_name,
            value: { type },
          })
        } else {
          useUserHighlightStore().unsetAndSave({ user: this.user.screen_name })
        }
      },
    },
    userHighlightColor: {
      get() {
        return useUserHighlightStore().get(this.user.screen_name).color
      },
      set(color) {
        useUserHighlightStore().setAndSave({
          user: this.user.screen_name,
          value: { color },
        })
      },
    },
    visibleRole() {
      if (!this.user.show_role && !this.user.adminData) {
        return
      }
      const rights = this.user.rights
      if (!rights) {
        return
      }
      const validRole = rights.admin || rights.moderator
      const roleTitle = rights.admin ? 'admin' : 'moderator'
      return validRole && roleTitle
    },
    hideFollowsCount() {
      return this.isOtherUser && this.user.hide_follows_count
    },
    hideFollowersCount() {
      return this.isOtherUser && this.user.hide_followers_count
    },
    showModerationMenu() {
      const privileges = this.loggedIn.privileges
      return (
        this.loggedIn.role === 'admin' ||
        privileges.has('users_manage_activation_state') ||
        privileges.has('users_delete') ||
        privileges.has('users_manage_tags')
      )
    },
    hasNote() {
      return this.relationship.note
    },
    supportsNote() {
      return 'note' in this.relationship
    },
    formattedBirthday() {
      return formatLongDate(
        this.user.birthday,
        localeService.internalToBrowserLocale(this.$i18n.locale),
      )
    },
    formattedJoinDate() {
      return formatLongDate(
        this.user.created_at,
        localeService.internalToBrowserLocale(this.$i18n.locale),
      )
    },
    // null lets the avatar fall back to the stored picture
    avatarImgSrc() {
      return this.preview?.avatar || null
    },
    bannerImgSrc() {
      return this.preview?.banner || this.user.cover_photo || this.defaultBanner
    },
    // the instance's file (hosted: a full URL on it already)
    defaultBanner() {
      return serverUrl(
        useInstanceStore().server,
        useInstanceStore().instanceIdentity.defaultBanner,
      )
    },
    allowNonSquareEmoji() {
      return this.mergedConfig.nonSquareEmoji
    },
    pauseMfm() {
      return this.mergedConfig.pauseMfm
    },
    scaleMfm() {
      return this.mergedConfig.scaleMfm
    },
    hideUserStats() {
      return this.mergedConfig.hideUserStats
    },
    hideRemarks() {
      return this.mergedConfig.userCardHidePersonalMarks
    },
    ...mapState(useMergedConfigStore, ['mergedConfig']),
  },
  methods: {
    isKnownTag(tag) {
      return KNOWN_TAGS.has(tag)
    },
    muteUser() {
      this.$refs.timedMuteDialog.optionallyPrompt()
    },
    unmuteUser() {
      return useUsersStore().unmuteUser(this.user.id)
    },
    subscribeUser() {
      return useUsersStore().subscribeUser(this.user.id)
    },
    unsubscribeUser() {
      return useUsersStore().unsubscribeUser(this.user.id)
    },
    userProfileLink: profileLink,
    openProfileTab() {
      useInterfaceStore().openSettingsModalTab('profile')
    },
    zoomAvatar() {
      const attachment = {
        url: this.user.profile_image_url_original,
        type: 'image',
      }
      useMediaViewerStore().setMedia([attachment])
      useMediaViewerStore().setCurrentMedia(attachment)
    },
    mentionUser() {
      usePostStatusStore().openPostStatusModal({
        profileMention: this.user,
      })
    },
  },
}
